import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useCriteriaStore } from '@/stores/criteria-store'
import { criteriaPresets, type UserCriteria } from '@/lib/schema'
import { clsx } from 'clsx'

export function SettingsPage() {
  const { criteria, isLoading, updateCriteria, fetchCriteria } = useCriteriaStore()
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isDirty },
  } = useForm<UserCriteria>({
    defaultValues: criteria || {},
  })

  useEffect(() => {
    fetchCriteria()
  }, [fetchCriteria])

  useEffect(() => {
    if (criteria) {
      reset(criteria)
    }
  }, [criteria, reset])

  const onSubmit = async (data: UserCriteria) => {
    setIsSaving(true)
    setSaveMessage(null)
    try {
      // Clean up empty values
      const cleanedData: UserCriteria = {}
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null && String(value) !== '') {
          cleanedData[key as keyof UserCriteria] = Number(value)
        }
      })

      await updateCriteria(cleanedData)
      setSaveMessage('Criteria saved successfully')
      setTimeout(() => setSaveMessage(null), 3000)
    } catch {
      setSaveMessage('Failed to save criteria')
    } finally {
      setIsSaving(false)
    }
  }

  const applyPreset = (preset: keyof typeof criteriaPresets) => {
    const values = criteriaPresets[preset]
    Object.entries(values).forEach(([key, value]) => {
      setValue(key as keyof UserCriteria, value, { shouldDirty: true })
    })
  }

  if (isLoading && !criteria) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6">
        <h1>Go/No-Go Criteria</h1>
        <p className="mt-1 text-slate-500">
          Set minimum thresholds for evaluating deals. Deals will be marked as pass/fail based on these criteria.
        </p>
      </div>

      {/* Presets */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <h3 className="font-medium text-slate-900">Quick Presets</h3>
        <p className="mt-1 text-sm text-slate-500">
          Start with a preset and customize as needed.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => applyPreset('conservative')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Conservative
          </button>
          <button
            type="button"
            onClick={() => applyPreset('moderate')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Moderate
          </button>
          <button
            type="button"
            onClick={() => applyPreset('aggressive')}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Aggressive
          </button>
          <button
            type="button"
            onClick={() => applyPreset('cleveland')}
            className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-100"
          >
            Cleveland Triplex/4plex (Recommended)
          </button>
        </div>
      </div>

      {/* Criteria Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Min Cap Rate */}
          <div>
            <label htmlFor="minCapRate" className="block text-sm font-medium text-slate-700">
              Minimum Cap Rate
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Higher is better. Conservative: 10%, Moderate: 8%, Aggressive: 6%
            </p>
            <div className="relative mt-2">
              <input
                id="minCapRate"
                type="number"
                step="0.5"
                {...register('minCapRate')}
                className="block w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="8"
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500">
                %
              </span>
            </div>
          </div>

          {/* Min Cash-on-Cash */}
          <div>
            <label htmlFor="minCashOnCash" className="block text-sm font-medium text-slate-700">
              Minimum Cash-on-Cash Return
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Higher is better. Conservative: 15%, Moderate: 10%, Aggressive: 8%
            </p>
            <div className="relative mt-2">
              <input
                id="minCashOnCash"
                type="number"
                step="0.5"
                {...register('minCashOnCash')}
                className="block w-full rounded-lg border border-slate-300 py-2 pl-3 pr-8 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="10"
              />
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500">
                %
              </span>
            </div>
          </div>

          {/* Max GRM */}
          <div>
            <label htmlFor="maxGRM" className="block text-sm font-medium text-slate-700">
              Maximum GRM
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Lower is better. Conservative: 8, Moderate: 10, Aggressive: 12
            </p>
            <input
              id="maxGRM"
              type="number"
              step="0.5"
              {...register('maxGRM')}
              className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="10"
            />
          </div>

          {/* Min DSCR */}
          <div>
            <label htmlFor="minDSCR" className="block text-sm font-medium text-slate-700">
              Minimum DSCR
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Higher is better. Lenders require 1.0-1.25. Conservative: 1.5, Moderate: 1.25
            </p>
            <input
              id="minDSCR"
              type="number"
              step="0.05"
              {...register('minDSCR')}
              className="mt-2 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              placeholder="1.25"
            />
          </div>

          {/* Min Monthly Cash Flow */}
          <div>
            <label htmlFor="minMonthlyCashFlow" className="block text-sm font-medium text-slate-700">
              Minimum Monthly Cash Flow
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Higher is better. Conservative: $300, Moderate: $200, Aggressive: $100
            </p>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                $
              </span>
              <input
                id="minMonthlyCashFlow"
                type="number"
                step="25"
                {...register('minMonthlyCashFlow')}
                className="block w-full rounded-lg border border-slate-300 py-2 pl-7 pr-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="200"
              />
            </div>
          </div>

          {/* Max Purchase Price */}
          <div>
            <label htmlFor="maxPurchasePrice" className="block text-sm font-medium text-slate-700">
              Maximum Purchase Price
            </label>
            <p className="mt-0.5 text-xs text-slate-500">
              Optional. Set your budget ceiling.
            </p>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-500">
                $
              </span>
              <input
                id="maxPurchasePrice"
                type="number"
                step="10000"
                {...register('maxPurchasePrice')}
                className="block w-full rounded-lg border border-slate-300 py-2 pl-7 pr-3 text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="500000"
              />
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="mt-6 flex items-center justify-between border-t border-slate-100 pt-6">
          <div>
            {saveMessage && (
              <p
                className={clsx(
                  'text-sm',
                  saveMessage.includes('success') ? 'text-success-600' : 'text-danger-600'
                )}
              >
                {saveMessage}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSaving || !isDirty}
            className="rounded-lg bg-primary-600 px-6 py-2 font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Criteria'}
          </button>
        </div>
      </form>

      {/* Explanation */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5">
        <h3 className="font-medium text-slate-900">How Go/No-Go Works</h3>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li>
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-success-100 text-xs font-medium text-success-700">
              P
            </span>
            <span className="ml-2">
              <strong>PASS</strong> - All criteria met
            </span>
          </li>
          <li>
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-warning-100 text-xs font-medium text-warning-700">
              W
            </span>
            <span className="ml-2">
              <strong>WARNING</strong> - 75%+ criteria met
            </span>
          </li>
          <li>
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-danger-100 text-xs font-medium text-danger-700">
              F
            </span>
            <span className="ml-2">
              <strong>FAIL</strong> - Less than 75% criteria met
            </span>
          </li>
        </ul>
      </div>
    </div>
  )
}
